import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'framer-motion';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { cities } from './citiesData';
import { Eye, EyeOff, Mail, User, Lock, MapPin, Phone, Building2, Sun, Moon, AlertCircle, CheckCircle, ArrowRight, Globe, Calendar, Upload, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { searchAddresses } from '../../services/locationService';
import { registerUser } from '../../services/supabaseService';
import AgreementModal from '../modals/AgreementModal';
import { termsAndConditions, privacyPolicy } from '../../data/agreements';
import { createClient } from '@supabase/supabase-js';
import VerificationModal from './VerificationModal';
import { format } from 'date-fns';
import TurnstileCaptcha from '../common/TurnstileCaptcha';

// Supabase client oluştur
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// GLOBAL Supabase client oluştur
/*
const supabase = createClient(
  'https://ysntdhepknjalzupojib.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlzbnRkaGVwa25qYWx6dXBvamliIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDEwNjU2ODYsImV4cCI6MjA1NjY0MTY4Nn0.zG8DgQ_DKENTdqSzcFjPKNnv5L1P4J9t3C6JAwXQRzI'
);

*/
// Custom CSS for react-select and phone input
const customStyles = `
  .react-select-container .react-select__control {
    min-height: 48px;
    padding-left: 2.75rem;
    background-color: transparent;
    border-radius: 0.75rem;
    border-color: rgba(229, 231, 235, 1);
    box-shadow: none;
  }

  .dark .react-select-container .react-select__control {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .react-select-container .react-select__control:hover {
    border-color: rgba(209, 213, 219, 1);
  }

  .dark .react-select-container .react-select__control:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .react-select-container .react-select__control--is-focused {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: transparent;
  }

  .react-select-container .react-select__menu {
    background-color: white;
    border-radius: 0.75rem;
    border: 1px solid rgba(229, 231, 235, 1);
    overflow: hidden;
  }

  .dark .react-select-container .react-select__menu {
    background-color: rgb(31, 41, 55);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .react-select-container .react-select__option {
    padding: 0.5rem 1rem;
    cursor: pointer;
  }

  .react-select-container .react-select__option--is-selected {
    background-color: rgb(59, 130, 246);
    color: white;
  }

  .react-select-container .react-select__option--is-focused:not(.react-select__option--is-selected) {
    background-color: rgba(243, 244, 246, 1);
  }

  .dark .react-select-container .react-select__option--is-focused:not(.react-select__option--is-selected) {
    background-color: rgba(59, 130, 246, 0.1);
  }

  .react-select-container .react-select__single-value {
    color: rgb(17, 24, 39);
  }

  .dark .react-select-container .react-select__single-value {
    color: white;
  }

  .react-select-container .react-select__input-container {
    color: rgb(17, 24, 39);
  }

  .dark .react-select-container .react-select__input-container {
    color: white;
  }

  .react-select-container .react-select__placeholder {
    color: rgb(107, 114, 128);
  }

  .dark .react-select-container .react-select__placeholder {
    color: rgb(156, 163, 175);
  }

  /* Phone Input Styles */
  .react-tel-input .form-control {
    width: 100% !important;
    height: 48px !important;
    padding-left: 5.5rem !important;
    background-color: transparent !important;
    border-radius: 0.75rem !important;
    border-color: rgba(229, 231, 235, 1) !important;
  }

  .dark .react-tel-input .form-control {
    background-color: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: white !important;
  }

  .react-tel-input .selected-flag {
    background-color: transparent !important;
    border-radius: 0.75rem 0 0 0.75rem !important;
  }

  .react-tel-input .country-list {
    border-radius: 0.75rem !important;
    border: 1px solid rgba(229, 231, 235, 1) !important;
    background-color: white !important;
  }

  .dark .react-tel-input .country-list {
    background-color: rgb(31, 41, 55) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }

  .react-tel-input .country-list .country:hover {
    background-color: rgba(243, 244, 246, 1) !important;
  }

  .dark .react-tel-input .country-list .country:hover {
    background-color: rgba(59, 130, 246, 0.1) !important;
  }

  .react-tel-input .country-list .country.highlight {
    background-color: rgb(59, 130, 246) !important;
    color: white !important;
  }
`;

const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('');
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    address: '',
    postalCode: '',
    tcNo: '',
    gender: '',
    birthDate: null,
    profilePhoto: null,
    position: '',
    website: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
    kvkkAccepted: false,
    newsletterSubscription: false,
    dataShareConsent: false
  });
  const [errors, setErrors] = useState({});
  const [emailError, setEmailError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef(null);
  const addressTimeoutRef = useRef(null);
  const addressDropdownRef = useRef(null);
  const [isWebsiteValid, setIsWebsiteValid] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const [privacyRead, setPrivacyRead] = useState(false);
  // Add new state for agreement warnings
  const [agreementWarnings, setAgreementWarnings] = useState({
    terms: false,
    privacy: false
  });
  // Form validasyonu için yeni state
  const [isFormValid, setIsFormValid] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Add style tag to head
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.textContent = customStyles;
    document.head.appendChild(styleTag);
    return () => styleTag.remove();
  }, []);

  // Custom styles for Select components
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '48px',
      paddingLeft: '2.75rem',
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'white',
      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb',
      borderRadius: '0.75rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      '&:hover': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
      }
    }),
    input: (base) => ({
      ...base,
      color: isDark ? 'white' : '#111827',
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9ca3af' : '#6b7280',
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? 'white' : '#111827',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? (isDark ? '#3b82f6' : '#3b82f6')
        : state.isFocused
          ? (isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6')
          : 'transparent',
      color: state.isSelected
        ? 'white'
        : isDark ? 'white' : '#111827',
      '&:hover': {
        backgroundColor: state.isSelected
          ? (isDark ? '#3b82f6' : '#3b82f6')
          : (isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6'),
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : 'white',
      borderRadius: '0.75rem',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
    })
  };

  // Custom theme for Select components
  const customSelectTheme = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary: '#3b82f6',
      primary25: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6',
      neutral0: isDark ? '#1f2937' : 'white',
      neutral80: isDark ? 'white' : '#111827',
    },
  });

  // Custom styles for phone input
  const phoneInputClass = `w-full pl-12 pr-4 py-3 ${
    isDark 
      ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`;

  const genderOptions = [
    { value: 'male', label: 'Erkek' },
    { value: 'female', label: 'Kadın' },
    { value: 'other', label: 'Diğer' }
  ];

  const positionOptions = [
    // Yönetim ve İdari Pozisyonlar
    { value: 'CEO (Genel Müdür)', label: 'CEO (Genel Müdür)' },
    { value: 'COO (Operasyon Direktörü)', label: 'COO (Operasyon Direktörü)' },
    { value: 'CFO (Finans Direktörü)', label: 'CFO (Finans Direktörü)' },
    { value: 'Genel Müdür', label: 'Genel Müdür' },
    { value: 'İK Müdürü', label: 'İK Müdürü' },
    { value: 'Proje Yöneticisi', label: 'Proje Yöneticisi' },
    { value: 'Satınalma Müdürü', label: 'Satınalma Müdürü' },
    { value: 'Operasyon Yöneticisi', label: 'Operasyon Yöneticisi' },
    { value: 'Pazarlama Müdürü', label: 'Pazarlama Müdürü' },
  
    // Mühendislik ve Teknik Pozisyonlar
    { value: 'Yazılım Mühendisi', label: 'Yazılım Mühendisi' },
    { value: 'Elektrik & Elektronik Mühendisi', label: 'Elektrik & Elektronik Mühendisi' },
    { value: 'Makine Mühendisi', label: 'Makine Mühendisi' },
    { value: 'İnşaat Mühendisi', label: 'İnşaat Mühendisi' },
    { value: 'Endüstri Mühendisi', label: 'Endüstri Mühendisi' },
    { value: 'Sistem Yöneticisi', label: 'Sistem Yöneticisi' },
    { value: 'Web Geliştirici', label: 'Web Geliştirici' },
    { value: 'Veritabanı Yöneticisi', label: 'Veritabanı Yöneticisi' },
    { value: 'Ağ Mühendisi', label: 'Ağ Mühendisi' },
    { value: 'CAD Tasarımcı', label: 'CAD Tasarımcı' },
    { value: '3D Modelleme Uzmanı', label: '3D Modelleme Uzmanı' },
  
    // Pazarlama ve Satış Pozisyonları
    { value: 'Pazarlama Uzmanı', label: 'Pazarlama Uzmanı' },
    { value: 'Dijital Pazarlama Uzmanı', label: 'Dijital Pazarlama Uzmanı' },
    { value: 'Satış Müdürü', label: 'Satış Müdürü' },
    { value: 'Satış Temsilcisi', label: 'Satış Temsilcisi' },
    { value: 'Satınalma Uzmanı', label: 'Satınalma Uzmanı' },
    { value: 'Ürün Yöneticisi', label: 'Ürün Yöneticisi' },
    { value: 'Sosyal Medya Yöneticisi', label: 'Sosyal Medya Yöneticisi' },
    { value: 'İhracat Satış Uzmanı', label: 'İhracat Satış Uzmanı' },
  
    // İnsan Kaynakları Pozisyonları
    { value: 'İnsan Kaynakları Uzmanı', label: 'İnsan Kaynakları Uzmanı' },
    { value: 'İşe Alım Uzmanı', label: 'İşe Alım Uzmanı' },
    { value: 'Performans Değerlendirme Uzmanı', label: 'Performans Değerlendirme Uzmanı' },
    { value: 'Eğitim ve Gelişim Uzmanı', label: 'Eğitim ve Gelişim Uzmanı' },
    { value: 'İK Direktörü', label: 'İK Direktörü' },
  
    // Hizmet ve Perakende Pozisyonları
    { value: 'Mağaza Müdürü', label: 'Mağaza Müdürü' },
    { value: 'Satış Elemanı', label: 'Satış Elemanı' },
    { value: 'Müşteri Temsilcisi', label: 'Müşteri Temsilcisi' },
    { value: 'Kasiyer', label: 'Kasiyer' },
    { value: 'Restoran Müdürü', label: 'Restoran Müdürü' },
    { value: 'Garson', label: 'Garson' },
    { value: 'Aşçı', label: 'Aşçı' },
  
    // Sağlık ve Sosyal Hizmetler Pozisyonları
    { value: 'Doktor', label: 'Doktor' },
    { value: 'Hemşire', label: 'Hemşire' },
    { value: 'Ebe', label: 'Ebe' },
    { value: 'Psikolog', label: 'Psikolog' },
    { value: 'Fizyoterapist', label: 'Fizyoterapist' },
    { value: 'Diş Hekimi', label: 'Diş Hekimi' },
    { value: 'Sosyal Hizmet Uzmanı', label: 'Sosyal Hizmet Uzmanı' },
    { value: 'Sağlık Teknisyeni', label: 'Sağlık Teknisyeni' },
  
    // Finans ve Muhasebe Pozisyonları
    { value: 'Muhasebe Uzmanı', label: 'Muhasebe Uzmanı' },
    { value: 'Finansal Analist', label: 'Finansal Analist' },
    { value: 'Denetçi', label: 'Denetçi' },
    { value: 'Vergi Uzmanı', label: 'Vergi Uzmanı' },
    { value: 'Hazine Yöneticisi', label: 'Hazine Yöneticisi' },
    { value: 'Banka Memuru', label: 'Banka Memuru' },
  
    // Yaratıcı ve Tasarım Pozisyonları
    { value: 'Grafik Tasarımcı', label: 'Grafik Tasarımcı' },
    { value: 'Web Tasarımcısı', label: 'Web Tasarımcısı' },
    { value: 'İç Mekan Tasarımcısı', label: 'İç Mekan Tasarımcısı' },
    { value: 'Moda Tasarımcısı', label: 'Moda Tasarımcısı' },
    { value: 'Video Editörü', label: 'Video Editörü' },
    { value: 'Animatör', label: 'Animatör' },
    { value: 'Fotoğrafçı', label: 'Fotoğrafçı' },
    { value: 'Sanat Direktörü', label: 'Sanat Direktörü' },
  
    // Eğitim ve Akademik Pozisyonlar
    { value: 'Öğretmen', label: 'Öğretmen' },
    { value: 'Eğitim Uzmanı', label: 'Eğitim Uzmanı' },
    { value: 'Rehberlik ve Psikolojik Danışman', label: 'Rehberlik ve Psikolojik Danışman' },
    { value: 'Akademisyen', label: 'Akademisyen' },
    { value: 'Eğitim Koçu', label: 'Eğitim Koçu' },
    { value: 'Okul Müdürü', label: 'Okul Müdürü' },
  
    // Lojistik ve Depo Yönetimi Pozisyonları
    { value: 'Lojistik Yöneticisi', label: 'Lojistik Yöneticisi' },
    { value: 'Depo Sorumlusu', label: 'Depo Sorumlusu' },
    { value: 'Sevkiyat Koordinatörü', label: 'Sevkiyat Koordinatörü' },
    { value: 'Tedarik Zinciri Uzmanı', label: 'Tedarik Zinciri Uzmanı' },
    { value: 'Forklift Operatörü', label: 'Forklift Operatörü' },
  
    // Hukuk ve Danışmanlık Pozisyonları
    { value: 'Avukat', label: 'Avukat' },
    { value: 'Hukuk Müşaviri', label: 'Hukuk Müşaviri' },
    { value: 'İcra Memuru', label: 'İcra Memuru' },
    { value: 'Patent Uzmanı', label: 'Patent Uzmanı' },
    { value: 'Danışman', label: 'Danışman' },
  
    // İnşaat ve Yapı Pozisyonları
    { value: 'İnşaat Mühendisi', label: 'İnşaat Mühendisi' },
    { value: 'Proje Koordinatörü', label: 'Proje Koordinatörü' },
    { value: 'Elektrik Teknikeri', label: 'Elektrik Teknikeri' },
    { value: 'İnşaat Ustası', label: 'İnşaat Ustası' },
    { value: 'Şantiye Şefi', label: 'Şantiye Şefi' },
    { value: 'Yapı Ressamı', label: 'Yapı Ressamı' },
  
    // Bilgi Teknolojileri Pozisyonları
    { value: 'Yazılım Geliştiricisi', label: 'Yazılım Geliştiricisi' },
    { value: 'Sistem Analisti', label: 'Sistem Analisti' },
    { value: 'Test Uzmanı', label: 'Test Uzmanı' },
    { value: 'UI/UX Tasarımcı', label: 'UI/UX Tasarımcı' },
    { value: 'Teknik Destek Uzmanı', label: 'Teknik Destek Uzmanı' },
    { value: 'Siber Güvenlik Uzmanı', label: 'Siber Güvenlik Uzmanı' },
    { value: 'Lise Stajyeri', label: 'Lise Stajyeri' },
    { value: 'Üniversite Stajyeri', label: 'Üniversite Stajyeri' },
    { value: 'Diğer..', label: 'Diğer..' },
  ];
  


  const countryOptions = [
      { value: 'Türkiye', label: 'Türkiye' },
      { value: 'Amerika Birleşik Devletleri', label: 'Amerika Birleşik Devletleri' },
      { value: 'Birleşik Krallık', label: 'Birleşik Krallık' },
      { value: 'Almanya', label: 'Almanya' },
      { value: 'Fransa', label: 'Fransa' },
      { value: 'İtalya', label: 'İtalya' },
      { value: 'İspanya', label: 'İspanya' },
      { value: 'Polonya', label: 'Polonya' },
      { value: 'Rusya', label: 'Rusya' },
      { value: 'Hindistan', label: 'Hindistan' },
      { value: 'Çin', label: 'Çin' },
      { value: 'Japonya', label: 'Japonya' },
      { value: 'Güney Kore', label: 'Güney Kore' },
      { value: 'Brezilya', label: 'Brezilya' },
      { value: 'Kanada', label: 'Kanada' },
      { value: 'Avustralya', label: 'Avustralya' },
      { value: 'Meksika', label: 'Meksika' },
      { value: 'Güney Afrika', label: 'Güney Afrika' },
      { value: 'Nijerya', label: 'Nijerya' },
      { value: 'Mısır', label: 'Mısır' },
      { value: 'Arjantin', label: 'Arjantin' },
      { value: 'Şili', label: 'Şili' },
      { value: 'Kolombiya', label: 'Kolombiya' },
      { value: 'Peru', label: 'Peru' },
      { value: 'Singapur', label: 'Singapur' },
      { value: 'Malezya', label: 'Malezya' },
      { value: 'Filipinler', label: 'Filipinler' },
      { value: 'Tayland', label: 'Tayland' },
      { value: 'Vietnam', label: 'Vietnam' },
      { value: 'Endonezya', label: 'Endonezya' },
      { value: 'Kore Cumhuriyeti', label: 'Kore Cumhuriyeti' },
      { value: 'Suudi Arabistan', label: 'Suudi Arabistan' },
      { value: 'Birleşik Arap Emirlikleri', label: 'Birleşik Arap Emirlikleri' },
      { value: 'Kuveyt', label: 'Kuveyt' },
      { value: 'Katar', label: 'Katar' },
      { value: 'Umman', label: 'Umman' },
      { value: 'Bahreyn', label: 'Bahreyn' },
      { value: 'Sri Lanka', label: 'Sri Lanka' },
      { value: 'Bangladeş', label: 'Bangladeş' },
      { value: 'Pakistan', label: 'Pakistan' },
      { value: 'Yeni Zelanda', label: 'Yeni Zelanda' },
      { value: 'Finlandiya', label: 'Finlandiya' },
      { value: 'İsveç', label: 'İsveç' },
      { value: 'Norveç', label: 'Norveç' },
      { value: 'Danimarka', label: 'Danimarka' },
      { value: 'İzlanda', label: 'İzlanda' },
      { value: 'Yunanistan', label: 'Yunanistan' },
      { value: 'Macaristan', label: 'Macaristan' },
      { value: 'Avusturya', label: 'Avusturya' },
      { value: 'Belçika', label: 'Belçika' },
      { value: 'Hollanda', label: 'Hollanda' },
      { value: 'İsviçre', label: 'İsviçre' },
      { value: 'Çek Cumhuriyeti', label: 'Çek Cumhuriyeti' },
      { value: 'Slovakya', label: 'Slovakya' },
      { value: 'Romanya', label: 'Romanya' },
      { value: 'Bulgaristan', label: 'Bulgaristan' },
      { value: 'Ukrayna', label: 'Ukrayna' },
      { value: 'Beyaz Rusya', label: 'Beyaz Rusya' },
      { value: 'Moldova', label: 'Moldova' },
      { value: 'Azerbaycan', label: 'Azerbaycan' },
      { value: 'Gürcistan', label: 'Gürcistan' },
      { value: 'Ermenistan', label: 'Ermenistan' },
      { value: 'Kazakistan', label: 'Kazakistan' },
      { value: 'Kırgızistan', label: 'Kırgızistan' },
      { value: 'Tacikistan', label: 'Tacikistan' },
      { value: 'Türkmenistan', label: 'Türkmenistan' },
      { value: 'Özbekistan', label: 'Özbekistan' },
      { value: 'Laos', label: 'Laos' },
      { value: 'Myanmar', label: 'Myanmar' },
      { value: 'Kamboçya', label: 'Kamboçya' },
      { value: 'Butan', label: 'Butan' },
      { value: 'Nepal', label: 'Nepal' },
      { value: 'Maldivler', label: 'Maldivler' },
      { value: 'Timor-Leste', label: 'Timor-Leste' },
      { value: 'Karadağ', label: 'Karadağ' },
      { value: 'Sırbistan', label: 'Sırbistan' },
      { value: 'Hırvatistan', label: 'Hırvatistan' },
      { value: 'Bosna-Hersek', label: 'Bosna-Hersek' },
      { value: 'Kuzey Makedonya', label: 'Kuzey Makedonya' },
      { value: 'Arnavutluk', label: 'Arnavutluk' },
      { value: 'Lihtenştayn', label: 'Lihtenştayn' },
      { value: 'San Marino', label: 'San Marino' },
      { value: 'Monako', label: 'Monako' },
      { value: 'Lüksemburg', label: 'Lüksemburg' },
      { value: 'Malta', label: 'Malta' },
      { value: 'Kıbrıs', label: 'Kıbrıs' },
      { value: 'Benin', label: 'Benin' },
      { value: 'Kamerun', label: 'Kamerun' },
      { value: 'Kenya', label: 'Kenya' },
      { value: 'Gana', label: 'Gana' },
      { value: 'Tanzanya', label: 'Tanzanya' },
      { value: 'Zimbabve', label: 'Zimbabve' },
      { value: 'Malavi', label: 'Malavi' },
      { value: 'Senegal', label: 'Senegal' },
      { value: 'Uganda', label: 'Uganda' },
      { value: 'Ruanda', label: 'Ruanda' },
      { value: 'Etiyopya', label: 'Etiyopya' },
      { value: 'Kongolu Demokratik Cumhuriyeti', label: 'Kongolu Demokratik Cumhuriyeti' },
      { value: 'Nijer', label: 'Nijer' },
      { value: 'Gambiya', label: 'Gambiya' },
      { value: 'Mali', label: 'Mali' },
      { value: 'Angola', label: 'Angola' },
      { value: 'Zimbabve', label: 'Zimbabve' },
      { value: 'Madagaskar', label: 'Madagaskar' },
      { value: 'Seyşeller', label: 'Seyşeller' },
      { value: 'Liberia', label: 'Liberia' },
      { value: 'Fiji', label: 'Fiji' },
      { value: 'Tonga', label: 'Tonga' },
      { value: 'Samoa', label: 'Samoa' },
      { value: 'Diğer..', label: 'Diğer..' }
    
    
];

  // Add these formatting functions after the initial state declarations
  const formatFirstName = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR'))
      .join(' ');
  };

  const formatLastName = (name) => {
    return name.toLocaleUpperCase('tr-TR');
  };

  // E-posta doğrulama fonksiyonu
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const commonDomains = ['gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 'teira3d.com', 'teira3d.com.tr','urety.co','beetrax.net'];
    
    if (!email) {
      setEmailError('E-posta adresi gerekli');
      setIsEmailValid(false);
      return false;
    }
    
    if (!emailRegex.test(email)) {
      setEmailError('Geçerli bir e-posta adresi giriniz');
      setIsEmailValid(false);
      return false;
    }

    const domain = email.split('@')[1];
    if (!commonDomains.includes(domain) && !domain.endsWith('.edu.tr') && !domain.endsWith('.gov.tr')) {
      setEmailError('Yaygın olmayan bir domain kullanıyorsunuz, lütfen kontrol edin');
      setIsEmailValid(true); // Domain uyarısı olsa da format doğru
      return true;
    }

    setEmailError('');
    setIsEmailValid(true);
    return true;
  };

  // E-posta değiştiğinde kontrol et
  useEffect(() => {
    if (formData.email) {
      validateEmail(formData.email);
    }
  }, [formData.email]);

  // TC Kimlik Numarası doğrulama
  const validateTCNo = (tcno) => {
    if (!tcno) return false;
    if (tcno.length !== 11) return false;
    if (tcno[0] === '0') return false;
    
    // Sadece rakam kontrolü
    if (!/^\d+$/.test(tcno)) return false;
    
    let oddSum = 0;
    let evenSum = 0;
    let total = 0;
    
    for (let i = 0; i < 9; i++) {
      if (i % 2 === 0) {
        oddSum += parseInt(tcno[i]);
      } else {
        evenSum += parseInt(tcno[i]);
      }
      total += parseInt(tcno[i]);
    }
    
    if ((oddSum * 7 - evenSum) % 10 !== parseInt(tcno[9])) return false;
    if ((total + parseInt(tcno[9])) % 10 !== parseInt(tcno[10])) return false;
    
    return true;
  };

  // TC Kimlik için input kontrolü güncelleme
  const handleTCNoChange = (e) => {
    const value = e.target.value;
    
    // Sadece rakam girişine izin ver ve 11 karakterle sınırla
    if (value === '' || (/^\d+$/.test(value) && value.length <= 11)) {
      setFormData({ ...formData, tcNo: value });
      
      // Gerçek zamanlı doğrulama
      if (value.length === 11) {
        if (!validateTCNo(value)) {
          setErrors({ ...errors, tcNo: 'Geçersiz TC Kimlik Numarası' });
        } else {
          setErrors({ ...errors, tcNo: '' });
        }
      } else if (value.length > 0) {
        setErrors({ ...errors, tcNo: '11 haneli TC Kimlik Numarası giriniz' });
      } else {
        setErrors({ ...errors, tcNo: '' });
      }
    }
  };

  // Profil fotoğrafı yükleme
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        setErrors({...errors, profilePhoto: 'Dosya boyutu 25MB\'dan küçük olmalıdır'});
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setErrors({...errors, profilePhoto: 'Sadece JPG, PNG ve GIF formatları kabul edilir'});
        return;
      }

      setFormData({...formData, profilePhoto: file});
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setCaptchaError('');

    try {
      // CAPTCHA Token Kontrolü
      if (!captchaToken) {
        setCaptchaError('Lütfen CAPTCHA doğrulamasını tamamlayın');
        setIsSubmitting(false);
        return;
      }

      // E-posta kontrolü
      const { data: emailExists } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('email', formData.email)
        .maybeSingle();

      if (emailExists) {
        setSubmitError('Bu E-posta adresi zaten kullanılıyor!');
        setIsSubmitting(false);
        return;
      }

      // Telefon kontrolü
      const { data: phoneExists } = await supabase
        .from('user_profiles')
        .select('phone')
        .eq('phone', formData.phone)
        .maybeSingle();

      if (phoneExists) {
        setSubmitError('Bu Telefon numarası zaten kayıtlı!');
        setIsSubmitting(false);
        return;
      }

      // TC Kimlik kontrolü
      if (formData.tcNo) {
        const { data: nationalIdExists } = await supabase
          .from('user_profiles')
          .select('national_id')
          .eq('national_id', formData.tcNo)
          .maybeSingle();

        if (nationalIdExists) {
          setSubmitError('Bu TC Kimlik numarası zaten kayıtlı!');
          setIsSubmitting(false);
          return;
        }
      }

      // Cihaz ve bağlantı bilgilerini topla
      const deviceInfo = {
        local_ip_address: await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip),
        ip_address: await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip),
        device_name: navigator.userAgent.match(/\(([^)]+)\)/)[1], // Parantez içindeki bilgiyi al
        device_type: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        creation_date: format(new Date(), 'dd/MM/yy HH:mm:ss'),
        country: 'Unknown', // IP API'den gelecek
        address: 'Unknown', // IP API'den gelecek
        web_browser_name: navigator.userAgent.match(/(chrome|safari|firefox|msie|trident|edg)/i)?.[0] || 'Unknown',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        network_type: navigator.connection?.type || 'Unknown',
        isp_name: 'Unknown', // IP API'den gelecek
        browser_version: navigator.userAgent.match(/(?:chrome|safari|firefox|msie|trident|edg)\/?\s*(\d+)/i)?.[1] || 'Unknown',
        browser_language: navigator.language || 'Unknown',
        user_agent: navigator.userAgent,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen_orientation: screen.orientation?.type || window.orientation === 0 ? 'portrait' : 'landscape'
      };

      // IP bilgilerini al
      try {
        const ipResponse = await fetch('http://ip-api.com/json');
        const ipData = await ipResponse.json();
        
        if (ipData.status === 'success') {
          deviceInfo.country = ipData.countryCode;
          deviceInfo.address = `${ipData.city}, ${ipData.regionName}, ${ipData.country}`;
          deviceInfo.isp_name = ipData.isp;
          deviceInfo.time_zone = ipData.timezone;
        }
      } catch (error) {
        console.error('IP bilgileri alınamadı:', error);
      }

      // Doğrulama kodu oluştur
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 5); // 5 dakika geçerli

      // Doğrulama kodunu kaydet
      const { error: verificationError } = await supabase
        .from('verification_codes')
        .insert([{
          email: formData.email,
          code: verificationCode,
          expires_at: expiresAt.toISOString(),
          device_info: deviceInfo
        }]);

      if (verificationError) {
        throw verificationError;
      }

      // Email gönder
      const response = await fetch(`${import.meta.env.VITE_LOCALHOST_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: formData.email,
          subject: 'Email Doğrulama Kodu',
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Email Doğrulama</title>
              <style>
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body {
                  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  line-height: 1.5;
                  color: #1a1a1a;
                  background-color: #f5f5f5;
                }
                .container {
                  max-width: 600px;
                  margin: 40px auto;
                  background: #ffffff;
                  border-radius: 16px;
                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06);
                  overflow: hidden;
                }
                .header {
                  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                  padding: 32px;
                  text-align: center;
                  color: #ffffff;
                }
                .content {
                  padding: 32px;
                }
                .code-container {
                  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
                  border: 1px solid #e2e8f0;
                  border-radius: 12px;
                  padding: 24px;
                  margin: 24px 0;
                  text-align: center;
                  position: relative;
                }
                .code {
                  font-family: 'Courier New', monospace;
                  font-size: 32px;
                  font-weight: 700;
                  letter-spacing: 8px;
                  color: #1e40af;
                  background: #ffffff;
                  padding: 16px 24px;
                  border-radius: 8px;
                  border: 1px dashed #3b82f6;
                  display: inline-block;
                  margin-bottom: 12px;
                }
                .copy-text {
                  color: #6b7280;
                  font-size: 14px;
                  margin-top: 8px;
                }
                .info-box {
                  background: #f0f9ff;
                  border-left: 4px solid #3b82f6;
                  border-radius: 8px;
                  padding: 16px;
                  margin: 24px 0;
                }
                .info-title {
                  color: #1e40af;
                  font-weight: 600;
                  margin-bottom: 8px;
                }
                .info-list {
                  list-style-type: none;
                  margin: 0;
                  padding: 0;
                }
                .info-list li {
                  color: #1e3a8a;
                  font-size: 14px;
                  margin-bottom: 8px;
                  padding-left: 24px;
                  position: relative;
                }
                .info-list li:before {
                  content: "•";
                  color: #3b82f6;
                  font-weight: bold;
                  position: absolute;
                  left: 8px;
                }
                .footer {
                  background: #f8fafc;
                  padding: 24px;
                  text-align: center;
                  border-top: 1px solid #e2e8f0;
                }
                .footer p {
                  color: #64748b;
                  font-size: 14px;
                  margin: 4px 0;
                }
                .highlight {
                  color: #2563eb;
                  font-weight: 500;
                }
                .device-info {
                  background: #f8fafc;
                  border-radius: 8px;
                  padding: 16px;
                  margin: 24px 0;
                  font-size: 14px;
                }
                .device-info table {
                  width: 100%;
                  border-collapse: collapse;
                }
                .device-info td {
                  padding: 8px 0;
                  color: #64748b;
                }
                .device-info td:last-child {
                  text-align: right;
                  color: #0f172a;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <h1 style="font-size: 24px; margin-bottom: 8px;">Email Doğrulama</h1>
                  <p style="opacity: 0.9;">Hesabınızı güvence altına alın</p>
                </div>
                
                <div class="content">
                  <p style="font-size: 16px; color: #334155;">
                    Merhaba <span class="highlight">${formData.firstName} ${formData.lastName}</span>,
                  </p>
                  
                  <p style="margin: 16px 0; color: #334155;">
                    Hesabınızı doğrulamak için aşağıdaki kodu kullanın:
                  </p>

                  <div class="code-container">
                    <div class="code" onclick="navigator.clipboard.writeText('${verificationCode}')" style="cursor: pointer;">
                      ${verificationCode}
                    </div>
                    <div class="copy-text">
                      Kodu kopyalamak için tıklayın
                    </div>
                  </div>

                  <div class="info-box">
                    <div class="info-title">Önemli Güvenlik Bilgileri</div>
                    <ul class="info-list">
                      <li>Bu kod 5 dakika süreyle geçerlidir</li>
                      <li>Kodu kimseyle paylaşmayın</li>
                      <li>Yalnızca bir kez kullanılabilir</li>
                      <li>3 yanlış denemeden sonra kod geçersiz olur</li>
                      <li>Bu işlemi siz yapmadıysanız, lütfen dikkate almayın</li>
                    </ul>
                  </div>

                  <div class="device-info">
                    <table>
                      <tr>
                        <td>Cihaz:</td>
                        <td>${deviceInfo.device_name}</td>
                      </tr>
                      <tr>
                        <td>Tarayıcı:</td>
                        <td>${deviceInfo.web_browser_name} ${deviceInfo.browser_version}</td>
                      </tr>
                      <tr>
                        <td>Konum:</td>
                        <td>${deviceInfo.address}</td>
                      </tr>
                      <tr>
                        <td>IP Adresi:</td>
                        <td>${deviceInfo.ip_address}</td>
                      </tr>
                      <tr>
                        <td>Tarih:</td>
                        <td>${deviceInfo.creation_date}</td>
                      </tr>
                    </table>
                  </div>

                  <p style="margin: 24px 0; color: #334155; font-size: 14px;">
                    Güvenliğiniz bizim için önemli. Herhangi bir sorunuz olursa, 
                    <span class="highlight">destek ekibimizle</span> iletişime geçebilirsiniz.
                  </p>
                </div>

                <div class="footer">
                  <p style="font-weight: 500;">© ${new Date().getFullYear()} İş Takip Sistemi</p>
                  <p style="opacity: 0.7;">Bu e-posta otomatik olarak gönderilmiştir, lütfen yanıtlamayınız.</p>
                </div>
              </div>
            </body>
            </html>
          `
        })
      });

      if (!response.ok) {
        throw new Error('Email gönderilemedi');
      }

      // Doğrulama modalını göster
      setShowVerificationModal(true);
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSuccess = () => {
    setShowVerificationModal(false);
    navigate('/signin');
  };

  // Add this useEffect for handling clicks outside the address dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressDropdownRef.current && !addressDropdownRef.current.contains(event.target)) {
        setIsAddressDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this function for handling address input changes
  const handleAddressInputChange = async (value) => {
    setAddressInputValue(value);
    setFormData(prev => ({
      ...prev,
      address: value
    }));
    
    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
    }

    if (!value || value.length < 2) {
      setAddressSuggestions([]);
      setIsAddressDropdownOpen(false);
      return;
    }

    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const suggestions = await searchAddresses(value, formData.country);
        setAddressSuggestions(suggestions);
        setIsAddressDropdownOpen(suggestions.length > 0);
      } catch {
        setAddressSuggestions([]);
        setIsAddressDropdownOpen(false);
      }
    }, 300);
  };

  // Add this function for handling address selection
  const handleAddressSelect = (suggestion) => {
    setFormData({
      ...formData,
      address: suggestion.fullAddress,
    });
    setAddressInputValue(suggestion.fullAddress);
    setIsAddressDropdownOpen(false);
  };

  const validateUrl = (url) => {
    if (!url) {
      setWebsiteError('');
      setIsWebsiteValid(false);
      return true;
    }

    // URL formatını kontrol et
    const urlPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    if (!urlPattern.test(url)) {
      setWebsiteError('Geçerli bir web sitesi adresi giriniz');
      setIsWebsiteValid(false);
      return false;
    }

    // Yaygın TLD'leri kontrol et
    const commonTLDs = ['.com', '.net', '.org', '.edu', '.gov', '.mil', '.biz', '.info', '.io', '.co', '.me', '.tv', '.app', '.dev', '.tech', '.online', '.store', '.blog', '.site', '.web', '.xyz', '.tr'];
    if (!commonTLDs.some(tld => url.toLowerCase().endsWith(tld))) {
      setWebsiteError('Yaygın olmayan bir domain uzantısı kullanıyorsunuz, lütfen kontrol edin');
      setIsWebsiteValid(true); // Format doğru ama uyarı veriyoruz
      return true;
    }

    setWebsiteError('');
    setIsWebsiteValid(true);
    return true;
  };

  const handleWebsiteChange = (e) => {
    const value = e.target.value.trim();
    
    // URL'yi doğrula
    validateUrl(value);
    setFormData({ ...formData, website: value });
  };

  // Add this near the checkbox section:
  const allAgreementsRead = termsRead && privacyRead;

  // Update agreement handling
  useEffect(() => {
    if (allAgreementsRead) {
      setFormData(prev => ({
        ...prev,
        termsAccepted: true,
        kvkkAccepted: true
      }));
    }
  }, [allAgreementsRead]);

  // Update checkbox handlers
  const handleTermsCheckbox = (e) => {
    if (!termsRead) {
      setAgreementWarnings(prev => ({ ...prev, terms: true }));
      setTimeout(() => setAgreementWarnings(prev => ({ ...prev, terms: false })), 3000);
      return;
    }
    setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }));
  };

  const handleKvkkCheckbox = (e) => {
    if (!privacyRead) {
      setAgreementWarnings(prev => ({ ...prev, privacy: true }));
      setTimeout(() => setAgreementWarnings(prev => ({ ...prev, privacy: false })), 3000);
      return;
    }
    setFormData(prev => ({ ...prev, kvkkAccepted: e.target.checked }));
  };

  // Password strength calculation function
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    setPasswordStrength(strength);
    
    switch (strength) {
      case 0:
      case 1:
        setPasswordFeedback('Çok Zayıf');
        break;
      case 2:
        setPasswordFeedback('Zayıf');
        break;
      case 3:
        setPasswordFeedback('Orta');
        break;
      case 4:
        setPasswordFeedback('Güçlü');
        break;
      case 5:
        setPasswordFeedback('Çok Güçlü');
        break;
      default:
        setPasswordFeedback('');
    }
    
    return strength;
  };

  // Generate secure password
  const generateSecurePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let password = '';
    
    // Ensure at least one of each required character type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*()_+'[Math.floor(Math.random() * 12)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData(prev => ({
      ...prev,
      password,
      confirmPassword: password
    }));
    calculatePasswordStrength(password);
  };

  // Copy password to clipboard
  const copyPasswordToClipboard = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        throw new Error("Clipboard API desteklenmiyor");
      }
      
      if (!formData.password) {
        throw new Error("Şifre boş!");
      }
  
      await navigator.clipboard.writeText(formData.password);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 2000);
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  };
  

  // Handle password change
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData(prev => ({
      ...prev,
      password: newPassword
    }));
    calculatePasswordStrength(newPassword);
    
    // Check if passwords match
    if (formData.confirmPassword && formData.confirmPassword !== newPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Şifreler eşleşmiyor' }));
    } else {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, confirmPassword: value }));
    
    if (value && value !== formData.password) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Şifreler eşleşmiyor' }));
    } else {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  // Form validasyonu için useEffect
  useEffect(() => {
    const isTcNoValid = !formData.tcNo || (formData.tcNo.length === 11 && validateTCNo(formData.tcNo));
    
    const isFormComplete = 
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.phone.trim() !== '' &&
      formData.country !== '' &&
      formData.city !== '' &&
      formData.address.trim() !== '' &&
      formData.gender !== '' &&
      formData.birthDate !== null &&
      formData.position !== '' &&
      formData.password.trim() !== '' &&
      formData.confirmPassword.trim() !== '' &&
      formData.password === formData.confirmPassword &&
      formData.termsAccepted &&
      formData.kvkkAccepted &&
      isTcNoValid &&
      !errors.tcNo; // TC Kimlik hatası yoksa

    setIsFormValid(isFormComplete);
  }, [formData, errors.tcNo]);

  // CAPTCHA Callback Fonksiyonları
  const handleCaptchaVerify = (token) => {
    // console.log('CAPTCHA doğrulandı:', token);
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpire = () => {
    // console.log('CAPTCHA süresi doldu');
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA süresi doldu. Lütfen tekrar doğrulayın.');
  };

  const handleCaptchaError = (error) => {
    console.error('CAPTCHA hatası:', error);
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
    } py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="absolute inset-0">
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${
          isDark ? 'opacity-10' : 'opacity-20'
        }`}></div>
      </div>

      {/* Tema Değiştirme Butonu */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 p-2 rounded-full ${
          isDark ? 'bg-gray-800 text-yellow-400' : 'bg-white text-gray-800'
        } shadow-lg hover:scale-110 transition-all duration-200`}
      >
        {isDark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-4xl mx-auto"
      >
        <div className="w-full">
          {/* Form */}
          <div className={`${
            isDark ? 'bg-white/10 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200'
          } p-8 lg:p-12 rounded-3xl border shadow-2xl`}>
            <div className="flex items-center space-x-3 mb-8">
              <Building2 className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Yeni Hesap Oluştur
              </h2>
            </div>

            {/* Geri Dön Butonu */}
            <motion.button
              onClick={() => navigate('/signin')}
              className={`group mb-8 flex items-center text-sm font-medium ${
                isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              } transition-colors`}
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Ana sayfaya dön
            </motion.button>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {/* Kişisel Bilgiler */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Ad
                  </label>
                  <div className="relative">
                    <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: formatFirstName(e.target.value) })}
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="Adınız"
                      required
                    />
                  </div>
                  {errors.firstName && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.firstName}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Soyad
                  </label>
                  <div className="relative">
                    <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: formatLastName(e.target.value) })}
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="Soyadınız"
                    />
                  </div>
                  {errors.lastName && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.lastName}
                    </motion.p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    E-posta
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 ${
                        emailError && formData.email 
                          ? 'border-red-500 focus:ring-red-500' 
                          : isEmailValid 
                            ? 'border-green-500 focus:ring-green-500' 
                            : 'focus:ring-blue-500'
                      } focus:border-transparent transition-all duration-200`}
                      placeholder="ornek@sirket.com"
                    />
                    {formData.email && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {isEmailValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {emailError && formData.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {emailError}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Telefon
                  </label>
                  <div className="relative w-full">
  {/* Telefon ikonu */}
  <Phone 
    className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
      isDark ? 'text-gray-400' : 'text-gray-500'
    }`} 
  />

  {/* PhoneInput bileşeni */}
  <PhoneInput
    country="tr"
    value={formData.phone}
    onChange={(phone) => setFormData({ ...formData, phone })}
    containerClass="relative !w-full"
    inputClass={`!w-full !pl-12 !pr-4 !py-3 ${
      isDark
        ? '!bg-white/5 !border-white/10 !text-white !placeholder-gray-400'
        : '!bg-white !border-gray-200 !text-gray-900 !placeholder-gray-400'
    } !border !rounded-xl focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent transition-all duration-200`}
    buttonClass="!absolute !left-12 !top-1/2 !transform !-translate-y-1/2 !bg-transparent !border-0"
    dropdownClass={`${isDark ? '!bg-gray-800 !text-white' : '!bg-white'} !rounded-xl !border !border-gray-200`}
    searchClass={`${isDark ? '!bg-gray-700 !text-white' : '!bg-gray-50'} !rounded-lg !my-2`}
    enableSearch
    disableSearchIcon
    searchPlaceholder="Ülke ara..."
    countryCodeEditable={false}
    disableCountryCode={false}
    disableDropdown={false}
  />
</div>


                  {errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.phone}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Ülke
                  </label>
                  <div className="relative">
                    <Globe className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <Select
                      options={countryOptions}
                      value={countryOptions.find(option => option.value === formData.country)}
                      onChange={(option) => setFormData({ ...formData, country: option.value })}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Ülke seçiniz"
                      theme={theme => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: '#3b82f6',
                          primary25: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6',
                          neutral0: isDark ? '#1f2937' : 'white',
                          neutral80: isDark ? 'white' : '#111827',
                        },
                      })}
                    />
                  </div>
                  {errors.country && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.country}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Şehir
                  </label>
                  <div className="relative">
  <MapPin className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
    isDark ? 'text-gray-400' : 'text-gray-500'
  }`} />
  <Select
    options={cities.map(city => ({ value: city.name, label: city.name }))}
    value={cities.find(city => city.name === formData.city) ? { value: formData.city, label: formData.city } : null}
    onChange={(option) => setFormData({ ...formData, city: option.value })}
    className="react-select-container"
    classNamePrefix="react-select"
    placeholder="Şehir seçiniz"
    theme={theme => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#3b82f6',
        primary25: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6',
        neutral0: isDark ? '#1f2937' : 'white',
        neutral80: isDark ? 'white' : '#111827',
      },
    })}
  />
</div>
                  {errors.city && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.city}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    TC Kimlik Numarası
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.tcNo}
                      onChange={handleTCNoChange}
                      maxLength="11"
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                        errors.tcNo ? 'border-red-500' : formData.tcNo.length === 11 && validateTCNo(formData.tcNo) ? 'border-green-500' : ''
                      }`}
                      placeholder="TC Kimlik Numarası"
                      required
                    />
                    {formData.tcNo && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {formData.tcNo.length === 11 && validateTCNo(formData.tcNo) ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {errors.tcNo && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.tcNo}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Şifre
                  </label>
                  <div className="relative">
                    <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handlePasswordChange}
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="Şifreniz"
                    />
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`p-1.5 rounded-lg ${
                          isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                        } transition-colors duration-200`}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-500" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Password Strength Indicator and Actions */}
                  <div className="mt-2 space-y-2">
                    {/* Strength Bar */}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            passwordStrength === 0 ? 'w-0' :
                            passwordStrength === 1 ? 'w-1/5 bg-red-500' :
                            passwordStrength === 2 ? 'w-2/5 bg-orange-500' :
                            passwordStrength === 3 ? 'w-3/5 bg-yellow-500' :
                            passwordStrength === 4 ? 'w-4/5 bg-lime-500' :
                            'w-full bg-green-500'
                          }`}
                        />
                      </div>
                      <span className={`text-sm ${
                        passwordStrength <= 1 ? 'text-red-500' :
                        passwordStrength === 2 ? 'text-orange-500' :
                        passwordStrength === 3 ? 'text-yellow-500' :
                        passwordStrength === 4 ? 'text-lime-500' :
                        'text-green-500'
                      }`}>
                        {passwordFeedback}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={generateSecurePassword}
                        className={`text-sm px-3 py-1.5 rounded-lg ${
                          isDark
                            ? 'bg-white/10 hover:bg-white/20 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        } transition-colors duration-200 flex items-center space-x-1`}
                      >
                        <Lock className="w-4 h-4" />
                        <span>Güvenli Şifre Öner</span>
                      </button>
                      
                      {formData.password && (
                        <button
                          type="button"
                          onClick={copyPasswordToClipboard}
                          className={`text-sm px-3 py-1.5 rounded-lg ${
                            isDark
                              ? 'bg-white/10 hover:bg-white/20 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          } transition-colors duration-200`}
                        >
                          Kopyala
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Copy Notification - Moving outside of the form to the bottom-left corner */}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Şifre Tekrar
                  </label>
                  <div className="relative">
                    <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="Şifrenizi tekrar girin"
                    />
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`p-1.5 rounded-lg ${
                          isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                        } transition-colors duration-200`}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-500" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.confirmPassword}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Doğum Tarihi
                  </label>
                  <div className="relative">
                    <Calendar className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <DatePicker
                      selected={formData.birthDate}
                      onChange={(date) => {
                        // Ensure the date is set to noon to avoid timezone issues
                        if (date) {
                          const adjustedDate = new Date(date);
                          adjustedDate.setHours(12, 0, 0, 0);
                          setFormData({ ...formData, birthDate: adjustedDate });
                        } else {
                          setFormData({ ...formData, birthDate: null });
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showYearDropdown
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholderText="DD/MM/YYYY"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Cinsiyet
                  </label>
                  <div className="relative">
                    <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <Select
                      options={genderOptions}
                      value={genderOptions.find(option => option.value === formData.gender)}
                      onChange={(option) => setFormData({ ...formData, gender: option.value })}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Cinsiyet seçiniz"
                      theme={theme => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: '#3b82f6',
                          primary25: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6',
                          neutral0: isDark ? '#1f2937' : 'white',
                          neutral80: isDark ? 'white' : '#111827',
                        },
                      })}
                      required
                      isRequired
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Çalışma Pozisyonu
                  </label>
                  <div className="relative">
                    <Building2 className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <Select
                      options={positionOptions}
                      value={positionOptions.find(option => option.value === formData.position)}
                      onChange={(option) => setFormData({ ...formData, position: option.value })}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Pozisyon seçiniz"
                      theme={theme => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: '#3b82f6',
                          primary25: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f3f4f6',
                          neutral0: isDark ? '#1f2937' : 'white',
                          neutral80: isDark ? 'white' : '#111827',
                        },
                      })}
                      required
                      isRequired
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Web Sitesi (Mevcut ise)
                  </label>
                  <div className="relative">
                    <Globe className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <div className={`absolute left-12 top-1/2 transform -translate-y-1/2 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    } select-none pointer-events-none`}>
                      https://
                    </div>
                    <input
                      type="text"
                      value={formData.website}
                      onChange={handleWebsiteChange}
                      className={`w-full pl-32 pr-12 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="example.com"
                    />
                    {formData.website && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {isWebsiteValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {websiteError && formData.website && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm flex items-center ${
                        isWebsiteValid ? 'text-yellow-500' : 'text-red-500'
                      }`}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {websiteError}
                    </motion.p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Açık Adres
                  </label>
                  <div className="relative" ref={addressDropdownRef}>
                    <MapPin className={`absolute left-4 top-4 w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <textarea
                      value={addressInputValue}
                      onChange={(e) => handleAddressInputChange(e.target.value)}
                      rows="3"
                      className={`w-full pl-12 pr-4 py-3 ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none`}
                      placeholder="Adresinizi yazmaya başlayın..."
                    />
                    {isAddressDropdownOpen && addressSuggestions.length > 0 && (
                      <div className={`absolute z-50 w-full mt-1 ${
                        isDark ? 'bg-gray-800' : 'bg-white'
                      } border ${
                        isDark ? 'border-gray-700' : 'border-gray-200'
                      } rounded-xl shadow-lg max-h-60 overflow-auto`}>
                        {addressSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            onClick={() => handleAddressSelect(suggestion)}
                            className={`w-full text-left px-4 py-2 ${
                              isDark 
                                ? 'hover:bg-gray-700 text-gray-200' 
                                : 'hover:bg-gray-50 text-gray-700'
                            } transition-colors duration-150 ease-in-out`}
                          >
                            {suggestion.address}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.address && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.address}
                    </motion.p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}>
                    Profil Fotoğrafı
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="profile-photo"
                      />
                      <label
                        htmlFor="profile-photo"
                        className={`flex items-center justify-center w-32 h-32 rounded-xl cursor-pointer ${
                          isDark 
                            ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        } border-2 border-dashed transition-all duration-200`}
                      >
                        {previewImage ? (
                          <img
                            src={previewImage}
                            alt="Profile preview"
                            className="w-full h-full object-cover rounded-xl"
                          />
                        ) : (
                          <Upload className={`w-8 h-8 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                        )}
                      </label>
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p>Desteklenen formatlar: JPG, JPEG, PNG, GIF</p>
                      <p>Maksimum dosya boyutu: 25MB</p>
                    </div>
                  </div>
                  {errors.profilePhoto && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    >
                      {errors.profilePhoto}
                    </motion.p>
                  )}
                </div>

                <div className="md:col-span-2 space-y-4">
                  {/* Turnstile CAPTCHA */}
                  <div className="space-y-2">
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Güvenlik Doğrulaması
                    </label>
                    <TurnstileCaptcha
                      ref={captchaRef}
                      onVerify={handleCaptchaVerify}
                      onExpire={handleCaptchaExpire}
                      onError={handleCaptchaError}
                      action="register"
                      className="flex justify-center"
                      size="normal"
                    />
                    {captchaError && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-500 flex items-center"
                      >
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {captchaError}
                      </motion.p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={formData.termsAccepted}
                        onChange={handleTermsCheckbox}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className={`ml-2 block text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <button
                          type="button"
                          onClick={() => setTermsModalOpen(true)}
                          className="text-blue-600 hover:text-blue-500"
                        >
                          Kullanım Koşullarını
                        </button>
                        {' '}okudum ve kabul ediyorum
                      </label>
                    </div>
                    {termsRead && <CheckCircle className="w-5 h-5 text-green-500" />}
                  </div>
                  {agreementWarnings.terms && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm text-red-500 mt-1"
                    >
                      Lütfen önce sözleşmeyi okuyunuz
                    </motion.p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="kvkk"
                        checked={formData.kvkkAccepted}
                        onChange={handleKvkkCheckbox}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className={`ml-2 block text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <button
                          type="button"
                          onClick={() => setPrivacyModalOpen(true)}
                          className="text-blue-600 hover:text-blue-500"
                        >
                          KVKK Aydınlatma Metnini
                        </button>
                        {' '}okudum ve onaylıyorum
                      </label>
                    </div>
                    {privacyRead && <CheckCircle className="w-5 h-5 text-green-500" />}
                  </div>
                  {agreementWarnings.privacy && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm text-red-500 mt-1"
                    >
                      Lütfen önce sözleşmeyi okuyunuz
                    </motion.p>
                  )}

                  {allAgreementsRead && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg text-sm flex items-center"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Tüm zorunlu sözleşmeler okundu
                    </motion.div>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newsletter"
                      checked={formData.newsletterSubscription}
                      onChange={(e) => setFormData(prev => ({ ...prev, newsletterSubscription: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="newsletter" className={`ml-2 block text-sm ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <span>E-posta bültenine abone olmak istiyorum (İsteğe bağlı)</span>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="dataShare"
                      checked={formData.dataShareConsent}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataShareConsent: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="dataShare" className={`ml-2 block text-sm ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <span>Verilerimin işlenmesine izin veriyorum (İsteğe bağlı)</span>
                    </label>
                  </div>
                </div>
              </div>

              {submitError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl ${
                    isDark ? 'bg-red-900/50 text-red-200' : 'bg-red-50 text-red-600'
                  }`}
                >
                  <p className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {submitError}
                  </p>
                </motion.div>
              )}

              {/* Kayıt Ol Butonu */}
              <motion.button
                whileHover={{ scale: isFormValid && captchaToken ? 1.01 : 1 }}
                whileTap={{ scale: isFormValid && captchaToken ? 0.99 : 1 }}
                type="submit"
                disabled={isSubmitting || !allAgreementsRead || !formData.termsAccepted || !formData.kvkkAccepted || !isFormValid || !captchaToken}
                className={`group relative w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium shadow-lg transition-all duration-200 ${
                  (isSubmitting || !allAgreementsRead || !formData.termsAccepted || !formData.kvkkAccepted || !isFormValid || !captchaToken) 
                    ? 'opacity-50 cursor-not-allowed from-gray-400 to-gray-500 hover:from-gray-400 hover:to-gray-500' 
                    : ''
                }`}
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <Lock className={`h-5 w-5 ${isFormValid ? 'text-blue-300 group-hover:text-blue-200' : 'text-gray-300'}`} aria-hidden="true" />
                </span>
                {isSubmitting ? 'Kaydediliyor...' : 'Hesap Oluştur'}
                <ArrowRight className={`ml-2 w-5 h-5 ${isFormValid ? 'text-blue-300 group-hover:text-blue-200 group-hover:translate-x-1' : 'text-gray-300'} transition-transform`} />
              </motion.button>

              <p className={`text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Zaten hesabınız var mı?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/signin')}
                  className={`font-medium ${
                    isDark 
                      ? 'text-blue-400 hover:text-blue-300' 
                      : 'text-blue-600 hover:text-blue-500'
                  } transition-colors`}
                >
                  Giriş yapın
                </button>
              </p>
            </motion.form>
          </div>
        </div>
      </motion.div>

      <AgreementModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        onAccept={() => {
          setTermsRead(true);
          setTermsModalOpen(false);
        }}
        title="Kullanım Koşulları"
        content={termsAndConditions}
      />

      <AgreementModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        onAccept={() => {
          setPrivacyRead(true);
          setPrivacyModalOpen(false);
        }}
        title="KVKK Aydınlatma Metni"
        content={privacyPolicy}
      />

      {/* Copy Notification Toast */}
      {showCopyNotification && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className={`fixed bottom-4 left-4 px-4 py-3 rounded-xl shadow-lg flex items-center space-x-2 z-50 ${
            isDark ? 'bg-gray-800 text-green-400 border border-green-500/20' : 'bg-white text-green-600 border border-green-100'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Şifre kopyalandı!</span>
        </motion.div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          {/* Blur Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            className={`relative px-8 py-6 rounded-2xl shadow-2xl ${
              isDark 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-100'
            } max-w-md w-full mx-4`}
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", duration: 0.5 }}
              className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                <CheckCircle className="w-8 h-8 text-green-600" />
              </motion.div>
            </motion.div>

            {/* Success Message */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <h3 className={`text-xl font-semibold mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Hesabınız Başarıyla Oluşturuldu!
              </h3>
              <motion.p
                className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                animate={{
                  opacity: [1, 0.5, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                Giriş sayfasına yönlendiriliyorsunuz
                <motion.span
                  animate={{
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    times: [0, 0.33, 1]
                  }}
                >
                  .
                </motion.span>
                <motion.span
                  animate={{
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    times: [0, 0.33, 1],
                    delay: 0.2
                  }}
                >
                  .
                </motion.span>
                <motion.span
                  animate={{
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    times: [0, 0.33, 1],
                    delay: 0.4
                  }}
                >
                  .
                </motion.span>
              </motion.p>
            </motion.div>

            {/* Loading Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 flex justify-center"
            >
              <div className="flex space-x-1">
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.8, 1]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: 0
                  }}
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                />
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.8, 1]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: 0.2
                  }}
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                />
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.8, 1]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: 0.4
                  }}
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}

      <VerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={formData.email}
        formData={formData}
        onSuccess={handleVerificationSuccess}
      />
    </div>
  );
};

export default RegisterForm; 